import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import ShellLocaleRuntime from '../runtime/ShellLocaleRuntime.jsx';

const AUTH_PATH_PATTERNS = [
  /^\/login\/?$/,
  /^\/forgetpassword\/?$/,
  /^\/resetpassword(\/success)?\/?$/,
  /^\/frontend_user\/invite\/accept\/?$/,
];

function isAuthPath(pathname) {
  return AUTH_PATH_PATTERNS.some((pattern) => pattern.test(pathname));
}

function hasStoredLoginToken() {
  const readToken = (key) => {
    const raw = localStorage.getItem(key);
    if (!raw) return false;

    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return Boolean(parsed.value || parsed.token);
      }
    } catch (e) {
      return true;
    }

    return true;
  };

  return readToken('LOGIN_TOKEN') || readToken('MERCHANT_LOGIN_TOKEN');
}

const MainApp = React.lazy(() => import('../runtime/App.jsx'));
const AuthApp = React.lazy(() => import('../runtime/AuthApp.jsx'));

function getEntryModulePath(pathname) {
  if (/^\/login\/?$/.test(pathname)) {
    return 'login';
  }
  if (/^\/forgetpassword\/?$/.test(pathname)) {
    return 'auth';
  }
  if (/^\/resetpassword(\/success)?\/?$/.test(pathname)) {
    return 'auth';
  }
  if (/^\/frontend_user\/invite\/accept\/?$/.test(pathname)) {
    return 'auth';
  }
  return null;
}

function AuthEntryLoader({ pathname }) {
  const [Entry, setEntry] = useState(null);

  useEffect(() => {
    let active = true;
    const loadEntry = async () => {
      setEntry(null);
      const entryType = getEntryModulePath(pathname);
      if (!entryType) {
        return;
      }
      const mod = await (entryType === 'login'
        ? import('./LoginRoute.jsx')
        : import('./AuthRoute.jsx'));
      if (!active) return;
      setEntry(() => mod.default);
    };

    loadEntry();

    return () => {
      active = false;
    };
  }, [pathname]);

  return Entry ? <Entry pathname={pathname} /> : null;
}

function RootSwitcher() {
  const location = useLocation();
  const isAuth = isAuthPath(location.pathname);
  const shouldRedirectToLogin = !isAuth && !hasStoredLoginToken();
  const fallback = isAuth ? (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: '46% 54%',
        background: '#f3f4f6',
      }}
    >
      <div style={{ background: '#f2f4f8' }} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
        }}
      >
        <div style={{ width: 420, maxWidth: 'calc(100% - 40px)' }}>
          <div
            style={{
              height: 42,
              width: 180,
              background: '#e5e7eb',
              borderRadius: 8,
              marginBottom: 26,
            }}
          />
          <div
            style={{
              height: 34,
              background: '#e5e7eb',
              borderRadius: 8,
              marginBottom: 14,
            }}
          />
          <div
            style={{
              height: 34,
              background: '#e5e7eb',
              borderRadius: 8,
              marginBottom: 14,
            }}
          />
          <div
            style={{
              height: 14,
              background: '#e5e7eb',
              borderRadius: 8,
              marginBottom: 18,
            }}
          />
          <div style={{ height: 32, background: '#e5e7eb', borderRadius: 8 }} />
        </div>
      </div>
    </div>
  ) : (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateRows: '44px 1fr',
        background: '#f3f4f6',
      }}
    >
      <div style={{ background: '#001a4d' }} />
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr' }}>
        <aside
          style={{
            background: '#f8f8f8',
            borderRight: '1px solid #ececec',
            padding: 12,
          }}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 14,
                margin: '10px 0',
                borderRadius: 8,
                background: '#e5e7eb',
              }}
            />
          ))}
        </aside>
        <main style={{ padding: 22 }}>
          <div
            style={{
              height: 84,
              borderRadius: 8,
              background: '#e5e7eb',
              marginBottom: 14,
            }}
          />
          <div
            style={{ height: 280, borderRadius: 8, background: '#e5e7eb' }}
          />
        </main>
      </div>
    </div>
  );

  if (shouldRedirectToLogin) {
    return <Navigate replace to="/login" />;
  }

  return isAuth ? (
    <ShellLocaleRuntime>
      <AuthApp>
        <React.Suspense fallback={fallback}>
          <AuthEntryLoader pathname={location.pathname} />
        </React.Suspense>
      </AuthApp>
    </ShellLocaleRuntime>
  ) : (
    <ShellLocaleRuntime>
      <React.Suspense fallback={fallback}>
        <MainApp />
      </React.Suspense>
    </ShellLocaleRuntime>
  );
}

export default function AppEntry() {
  return <RootSwitcher />;
}
